import find from 'lodash/find'
import map from 'lodash/map'
import { IApplicationState } from '@Site/rootReducer'

import { createSelector } from 'reselect'

const DEFAULT_MAX_FILE_SIZE = 10737418240 // 10 GB
const ENTERPRISE_OR_CUSTOM_SUBSCRIPTION_PLAN_NAME_PARTS = ['enterprise', 'custom']

export const getAccounts = (state: IApplicationState) => state.accounts.accounts

export const getCurrentAccountId = (state: IApplicationState) => state.accounts.currentAccountId

export const getSaving = (state: IApplicationState) => state.accounts.saving

export const getHasRenamedAccountSuccessfully = (state: IApplicationState) => state.accounts.hasRenamedAccountSuccessfully

export const getUploadingAvatar = (state: IApplicationState) => state.accounts.uploadingAvatar

export const getCurrentAccount = createSelector(
  [getAccounts, getCurrentAccountId],
  (accounts, currentAccountId) => find(accounts, account => account.id === currentAccountId)
)

export const getAccountIds = createSelector(
  [getAccounts],
  (accounts) => map(accounts, account => account.id)
)

export const getCurrentAccountSubscription = createSelector(
  [getCurrentAccount],
  (currentAccount) => currentAccount?.subscription
)

export const getCurrentAccountHasCurrentSubscription = createSelector(
  [getCurrentAccountSubscription],
  (subscription) => !!subscription?.attributes.isCurrent
)

export const getCurrentAccountSubscriptionAllowsDubbing = createSelector(
  [getCurrentAccountSubscription],
  (subscription) => !!subscription?.attributes.allowsVoice
)

export const getCurrentAccountSubscriptionAllowsVoiceCloning = createSelector(
  [getCurrentAccountSubscription],
  (subscription) => !!subscription?.attributes.allowsVoiceCloning
)

export const getCurrentAccountSubscriptionAllowsTranslationAdaptionForDubbing = createSelector(
  [getCurrentAccountSubscription],
  (subscription) => !!subscription?.attributes.allowsTranslationAdaptionForDubbing
)

export const getCurrentAccountSubscriptionAllowsLipSyncing = createSelector(
  [getCurrentAccountSubscription],
  (subscription) => !!subscription?.attributes.allowsLipSyncing
)

export const getCurrentAccountSubscriptionHasCreditLikeCharging = createSelector(
  [getCurrentAccountSubscription],
  (subscription) => !!subscription?.attributes.hasCreditLikeCharging
)

export const getCurrentAccountSubscriptionMaxLanguagesLimit = createSelector(
  [getCurrentAccountSubscription],
  (subscription) => subscription?.attributes.maxLanguagesLimit
)

export const getCurrentAccountSubscriptionMaxSpeakerVoicesLimit = createSelector(
  [getCurrentAccountSubscription],
  (subscription) => subscription?.attributes.maxSpeakerVoicesLimit
)

export const getCurrentAccountName = createSelector(
  [getCurrentAccount],
  (currentAccount) => currentAccount?.attributes.name || ''
)

export const getUserIsAccountAdmin = createSelector(
  [getCurrentAccount],
  (currentAccount) => !!currentAccount?.attributes.userIsAdmin
)

export const getCurrentAccountAdminEmails = createSelector(
  [getCurrentAccount],
  (currentAccount) => currentAccount?.attributes.adminEmails || []
)

export const getCurrentAccountAvailableTotalCredits = createSelector(
  [getCurrentAccount],
  (currentAccount) => currentAccount?.attributes.availableTotalCredits || 0,
)

export const getCurrentAccountAvailableAnalysisCredits = createSelector(
  [getCurrentAccount],
  (currentAccount) => currentAccount?.attributes.availableAnalysisCredits || 0,
)

export const getCurrentAccountHasEnterpriseOrCustomSubscription = createSelector(
  [getCurrentAccount],
  (currentAccount) => {
    const subscriptionPlan = currentAccount?.subscription?.attributes.plan
    return (!!subscriptionPlan && ENTERPRISE_OR_CUSTOM_SUBSCRIPTION_PLAN_NAME_PARTS.includes(subscriptionPlan.split('_')[0]))
  }
)

export const getCurrentAccountMaxFileSize = createSelector(
  [getCurrentAccount],
  (currentAccount) => currentAccount?.attributes.maxFileSize || DEFAULT_MAX_FILE_SIZE,
)

export const getCurrentAccountMaxNumberOfAccountUsers = createSelector(
  [getCurrentAccount],
  (currentAccount) => currentAccount?.attributes.maxNumberOfAccountUsers || 0,
)

export const getCurrentAccountAvatarUrl = createSelector(
  [getCurrentAccount],
  (currentAccount) => currentAccount?.attributes.avatarUrl,
)

export const getCurrentAccountTranslationProvider = createSelector(
  [getCurrentAccount],
  (currentAccount) => currentAccount?.attributes.translationProvider || '',
)

export const getCurrentAccountTranslationProviderId = createSelector(
  [getCurrentAccount],
  (currentAccount) => currentAccount?.attributes.translationProviderId || null,
)

export const getCurrentAccountTranslationProviderSecret = createSelector(
  [getCurrentAccount],
  (currentAccount) => currentAccount?.attributes.translationProviderSecret || null,
)

export const getCurrentAccountStripeTaxPercent = createSelector(
  [getCurrentAccount],
  (currentAccount) => currentAccount?.attributes.stripeTaxPercent
)

export const getCurrentAccountStripeCurrency = createSelector(
  [getCurrentAccount],
  (currentAccount) => currentAccount?.attributes.stripeCurrency
)

export const getCurrentAccountCreditPricePerHour = createSelector(
  [getCurrentAccount],
  (currentAccount) => currentAccount?.attributes.creditPricePerHour || 0
)
